import type { AcademyChart } from "@/lib/academy/content";

/**
 * Dependency-free SVG charts for Academy guides. Charts are rendered
 * server-side so they appear in the HTML, print cleanly to PDF and stay
 * readable without JavaScript. Values are always repeated as text for screen
 * readers and for print.
 */

const PALETTE = [
  "var(--chart-1, #c8102e)",
  "var(--chart-2, #1f3a5f)",
  "var(--chart-3, #d98b2b)",
  "var(--chart-4, #4b7f6d)",
  "var(--chart-5, #7a6f9b)",
];

function BarChart({ chart }: { chart: AcademyChart }) {
  const max = Math.max(...chart.data.map((d) => d.value), 1);
  return (
    <ul className="mt-4 space-y-3">
      {chart.data.map((d, i) => (
        <li key={d.label}>
          <div className="flex items-baseline justify-between gap-4 text-sm">
            <span className="font-medium">{d.label}</span>
            <span className="tabular-nums text-ink-muted">
              {d.value}
              {chart.unit ?? ""}
            </span>
          </div>
          <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-surface-muted">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(d.value / max) * 100}%`,
                background: PALETTE[i % PALETTE.length],
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function DonutChart({ chart }: { chart: AcademyChart }) {
  const total = chart.data.reduce((sum, d) => sum + d.value, 0) || 1;
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-8">
      <svg viewBox="0 0 160 160" role="img" aria-hidden="true" className="h-40 w-40 shrink-0">
        <g transform="rotate(-90 80 80)">
          {chart.data.map((d, i) => {
            const fraction = d.value / total;
            const dash = fraction * circumference;
            const el = (
              <circle
                key={d.label}
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth="28"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return el;
          })}
        </g>
      </svg>
      <ul className="min-w-[12rem] flex-1 space-y-2 text-sm">
        {chart.data.map((d, i) => (
          <li key={d.label} className="flex items-baseline justify-between gap-4">
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: PALETTE[i % PALETTE.length] }}
              />
              {d.label}
            </span>
            <span className="tabular-nums text-ink-muted">
              {d.value}
              {chart.unit ?? ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AcademyChartBlock({ chart }: { chart: AcademyChart }) {
  return (
    <figure className="my-6 break-inside-avoid rounded-lg border border-hairline bg-surface-muted/30 p-5">
      <figcaption className="text-sm font-bold">{chart.title}</figcaption>
      {chart.type === "donut" ? <DonutChart chart={chart} /> : <BarChart chart={chart} />}
      {chart.note && <p className="mt-4 text-xs text-ink-muted">{chart.note}</p>}
    </figure>
  );
}
